import { DatePicker, TimePicker, Input, Select, InputNumber } from "antd"
import { useEffect, useState } from "react"
import dayjs from 'dayjs';
import { toDatePickerValue } from '../../../utils/safeDate.js';
import UserRoleDropdown from "../../UserRoleDropdown";
import { useTranslation } from 'react-i18next';
import { useMasterOptionLabel } from "../../../hooks/useMasterOptionLabel";
import { isMultiSelectComparator, parseConditionValueList } from "../../../utils/formsHelper";

const ConditionValue = ({ condition, handleScenarioChange, scenarioIndex, conditionIndex, selectedElement, formElements = [], workflowSteps = [] }) => {
    const { t } = useTranslation();
    const { getOptionLabel } = useMasterOptionLabel();
    const [component, setComponent] = useState(null)
    const [options, setOptions] = useState(selectedElement?.options || [])
    const disabled = condition.comparator ? false : true
    let formType = selectedElement?.formType || "inputForm"

    useEffect(() => {
        if (formType === "radioField" || formType === "dropdownField" || formType === "multipleChoiceField") {
            if (selectedElement.options) {
                let reqOptions = selectedElement.options.map(opt => {
                    let reqLabel = ""
                    if (opt?.elementCode) {
                        reqLabel = opt.elementCode + " "
                    }
                    // A master backed option can carry a row uuid as its label, so it is
                    // resolved the same way the field itself renders it
                    reqLabel += getOptionLabel(selectedElement, opt)
                    if (opt?.abbreviation) {
                        reqLabel += " " + opt.abbreviation
                    }
                    return { label: reqLabel, value: opt.value }
                })
                reqOptions = [...new Map(reqOptions.map(item => [item["value"], item])).values()];
                if (selectedElement.other) {
                    reqOptions.push({ label: t('Other'), value: 'other' })
                }
                setOptions(reqOptions)
            } else {
                setOptions([])
            }
        }
    }, [selectedElement, getOptionLabel])

    useEffect(() => {
        if (condition.parent === "Workflow step") {
            setOptions(workflowSteps)
        }
    }, [condition])

    const multiSelect = isMultiSelectComparator(condition.comparator, formType)
    const valueNotRequired = ["isEmpty", "isNotEmpty", "isToday", "isEven", "isOdd"].includes(condition.comparator)

    const handleChange = (val) => {
        let dropVal = val
        if (multiSelect) {
            dropVal = Array.isArray(dropVal) ? JSON.stringify(val) : dropVal
        }
        handleScenarioChange("value", dropVal, scenarioIndex, conditionIndex)
    }

    const onSelect = (values) => {
        let currentKey = condition.parent.toLowerCase();
        let currentValues = values[currentKey]?.[0] ?? ""
        if(currentKey === "connecteduser"){
            currentValues = values
        }
        if(condition.parent === "Form field" && formType === "userForm") {
            currentValues = {...values}
        }
        handleScenarioChange("value", currentValues, scenarioIndex, conditionIndex)
    }

    const renderComponent = () => {
        if (valueNotRequired) {
            setComponent(null)
            return
        }

        let currentComponent = null;
        if (formType === "inputForm" || formType === "addressField" || formType === "contactField" || formType === "textAreaForm" || formType === "linkField") {
            currentComponent = <Input disabled={disabled} placeholder={t('Enter value')} value={condition.value} onChange={(e) => handleChange(e.target.value)} style={{ minWidth: "9.5rem", maxWidth: "9.5rem" }} />
        } else if (formType === "numberForm") {
            currentComponent = <InputNumber disabled={disabled} placeholder={t('Enter value')} value={condition.value} onChange={(val) => handleChange(val)} style={{ minWidth: "9.5rem", maxWidth: "9.5rem" }} />

        } else if (formType === "radioField" || formType === "multipleChoiceField" || formType === "dropdownField") {
            let dropValue = condition.value;
            if (multiSelect) {
                dropValue = parseConditionValueList(dropValue)
            }
            currentComponent = <Select {...(multiSelect && { mode: "multiple" })} disabled={false} placeholder={t('Select')} value={dropValue} options={options} onChange={(val) => handleChange(val)} style={{ minWidth: "9.5rem", maxWidth: "9.5rem", textAlign: "left" }} />
        } else if (formType === "dateForm") {
            // Check if the value is a dynamic value like $TODAY
            const isDynamicValue = condition.value === "$TODAY";
            const isStaticMode = condition.value === "$STATIC" || (condition.value && condition.value !== "$TODAY");
            const dateValueOptions = [
                { label: t('Today (Dynamic)'), value: "$TODAY" },
                { label: t('Static Date'), value: "$STATIC" }
            ];

            // Determine selected option
            let selectedOption = undefined;
            if (isDynamicValue) {
                selectedOption = "$TODAY";
            } else if (isStaticMode) {
                selectedOption = "$STATIC";
            }

            currentComponent = (
                <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                    <Select
                        placeholder={t('Value type')}
                        options={dateValueOptions}
                        value={selectedOption}
                        onChange={(val) => {
                            if (val === "$TODAY") {
                                handleChange("$TODAY");
                            } else {
                                handleChange("$STATIC"); // Mark as static mode, waiting for date selection
                            }
                        }}
                        style={{ minWidth: "6.5rem", maxWidth: "6.5rem" }}
                        disabled={disabled}
                    />
                    {isStaticMode && (
                        <DatePicker
                            disabled={disabled}
                            placeholder={t('Select')}
                            value={condition.value && condition.value !== "$TODAY" && condition.value !== "$STATIC" ? toDatePickerValue(condition.value) : null}
                            format="YYYY-MM-DD"
                            onChange={(date, dateString) => handleChange(dateString || "$STATIC")}
                            style={{ minWidth: "7rem", maxWidth: "7rem" }}
                        />
                    )}
                </div>
            );
        } else if (formType === "timeForm") {
            currentComponent = <TimePicker disabled={disabled} placeholder={t('Select time')} value={toDatePickerValue(condition.value)} format={"HH:mm"} use12Hours={false} onChange={(value) => handleChange(value)} style={{ minWidth: "9.5rem", maxWidth: "9.5rem" }} />

        } else if (formType === "userForm") {
            currentComponent = <div style={{ minWidth: "9.5rem", maxWidth: "9.5rem" }}>
                <UserRoleDropdown
                    className="compact-user-dropdown"
                    selectedOptions={{
                        user: condition?.value?.user || [],
                        group: condition?.value?.group || [],
                        role: [],
                        userPicker: []
                    }}
                    mode={condition.comparator === "containsAnyOf" ? "MULTIPLE" : "SINGLE"}
                    onSelect={onSelect}
                    tabsRequired={["user", "group"]}
                    getSpaceUsers={true} dropdownStyle={{ width: "15rem" }} />
            </div>
        }
        if (condition.parent === "User" || condition.parent === "Group" || condition.parent === "connectedUser") {
            currentComponent = <div style={{ minWidth: "9.5rem", maxWidth: "9.5rem" }}>
                <UserRoleDropdown
                    className="compact-user-dropdown"
                    selectedOptions={{
                        user: condition.parent == "User" ? (condition?.value ? [condition?.value] : []) : condition.parent === "connectedUser" ? (condition?.value?.user || []) : [],
                        group: condition.parent == "Group" ? (condition?.value ? [condition?.value] : []) : condition.parent === "connectedUser" ? (condition?.value?.group || []) : [],
                        role: [],
                        userPicker: condition.parent == "connectedUser" ? (condition?.value?.userPicker ? condition?.value.userPicker : []) : []
                    }}
                    onSelect={onSelect}
                    tabsRequired={condition.parent == "User" ? ["user"] : condition.parent == "connectedUser" ? ["user", "group", "userPicker"] : ["group"]}
                    getSpaceUsers={true} mode="SINGLE" dropdownStyle={{ width: "15rem" }} />
            </div>
        }
        setComponent(currentComponent)
    }

    useEffect(() => {
        // if(formType === "radioField" || formType === "multipleChoiceField" || formType === "dropdownField") {
        //     let fields = getAllWidgets(formElements)
        //     let reqField = fields?.find(field => field.id === condition.reference)

        //     if(reqField.options){
        //         setOptions(reqField.options.map(opt => {
        //             return { label: opt.label, value: opt.label}
        //         }))
        //     }
        // }
        renderComponent();

        // `options` is a dependency too: master labels resolve once the master list lands,
        // and the rendered select holds a snapshot of them
    }, [formType, options, condition, valueNotRequired])

    if (!component) return null

    return (
        <div style={{ minWidth: "9rem", width: "fit-content" }}>
            {component}
        </div>
    )
}

export default ConditionValue